import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

export interface MeterBarWidgetProps {
    /** Left-aligned label in the header row. */
    title: React.ReactNode;
    /** Fill fraction, 0–1. Clamped defensively; drives the bar width and the
     *  default `rightSlot` percentage. */
    fraction: number;
    /** Left-aligned trailing label under the bar. Rendered only when
     *  `bottomValues` is `true` (the default). */
    fillLabel?: React.ReactNode;
    /** Right-aligned trailing label under the bar. Rendered only when
     *  `bottomValues` is `true` (the default). */
    trailLabel?: React.ReactNode;
    /** Render the bottom trailing-label row (`fillLabel` left / `trailLabel`
     *  right). Default `true`. When `false`, that row — and its `mt-1.5`
     *  spacing — is omitted entirely, leaving a compact meter of just the
     *  header (title · rightSlot) + the bar. */
    bottomValues?: boolean;
    /** The node right of the title. Defaults to `${round(fraction*100)}%`
     *  (muted, numeric) — the progress-meter treatment. Callers that need a
     *  countdown or other readout pass their own node here. */
    rightSlot?: React.ReactNode;
    /** Tailwind bg-* class for the filled portion. Defaults to ember. */
    fillColor?: string;
}

/**
 * MeterBarWidget — a thin left-to-right progress bar with a title row and a
 * fill/trail label row. Domain-free Generic Component (GC): it renders only
 * the shell (title · rightSlot · bar · fillLabel · trailLabel) and composes
 * `Text` for typography. It knows nothing about programs, budgets, or
 * distributions — every value is a prop. Data Components (`ProgramProgress`,
 * `NextDistributionMeter`) compose it.
 *
 * Hoisted into `shared/widgets/yaas/programs` (from the app's
 * `pages/Programs/MeterBarWidget`) so the hoisted `ProgramProgress` /
 * `NextDistributionMeter` Views render fixture-driven in the "+New Program"
 * wiki doc block. The app keeps a re-export shim at the old path so
 * `yaas-client` compiles unchanged.
 */
export function MeterBarWidget({
    title,
    fraction,
    fillLabel,
    trailLabel,
    rightSlot,
    fillColor = "bg-ember",
    bottomValues = true
}: MeterBarWidgetProps): React.ReactElement {
    const pct = Math.min(100, Math.max(0, Math.round(fraction * 100)));
    return (
        <div>
            <div className="flex items-baseline justify-between gap-4">
                <Text variant="fine-print" className="text-foreground-base">
                    {title}
                </Text>
                {rightSlot ?? (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted font-numeric"
                    >
                        {pct}%
                    </Text>
                )}
            </div>
            <div className="mt-2 h-1.5 w-full bg-surface-sunken overflow-hidden">
                {/* width is data-driven, so it lives in inline style */}
                <div
                    className={`h-full ${fillColor}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            {bottomValues && (
                <div className="mt-1.5 flex items-baseline justify-between gap-4">
                    <Text
                        variant="fine-print"
                        className="text-foreground-muted"
                    >
                        {fillLabel}
                    </Text>
                    <Text
                        variant="fine-print"
                        className="text-foreground-muted"
                    >
                        {trailLabel}
                    </Text>
                </div>
            )}
        </div>
    );
}
